import type { GeneralDocument as ApiGeneralDocument, Invoice as ApiInvoice, OrderDetail as ApiOrderDetail } from "randmar-api-client";
import Reseller from "../reseller/Reseller";

export interface IDocument {
  comments?: string;
  orderNumber?: string;
  Location?: string;
  purchaseOrderNumber?: string;
  receiptNumber?: string;
  requisitionNumber?: string;
  DocumentNumber?: string;
  SubTotal?: number;
}

export class Document {
  documentNumber?: string;
  location?: string;
  subTotal?: number;

  public static convertFrom(source: IDocument): Document {
    const document = new Document();

    document.documentNumber = source.DocumentNumber;
    document.location = source.Location;
    document.subTotal = source.SubTotal

    return document;
  }
}

export class PurchaseOrder implements IDocument {
  billToAddress1?: string;
  billToAddress2?: string;
  billToCity?: string;
  billToName?: string;
  billToPostalCode?: string;
  billToProvince?: string;
  comments?: string;
  contactEmail?: string;
  expectedArrivalDate?: number;
  location?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  purchaseOrderDate?: number;
  purchaseOrderNumber?: string;
  shipToAddress1?: string;
  shipToAddress2?: string;
  shipToCity?: string;
  shipToName?: string;
  shipToPostalCode?: string;
  shipToProvince?: string;
  shipVia?: string;
  shipViaDescription?: string;
  subTotal?: number;
  termsCode?: string;
  vendorCode?: string;
  vendorName?: string;


  partNumbers?: PurchaseOrderPartNumber[];
  receipts?: Receipt[];

  public static convertFrom(source: unknown): PurchaseOrder {
    const src = source as {
      BillToAddress1?: string | null;
      BillToAddress2?: string | null;
      BillToCity?: string | null;
      BillToName?: string | null;
      BillToPostalCode?: string | null;
      BillToProvince?: string | null;
      Comments?: string | null;
      ContactEmail?: string | null;
      ExpectedArrivalDate?: number;
      Location?: string | null;
      ManufacturerId?: string | null;
      ManufacturerName?: string | null;
      PurchaseOrderDate?: number;
      PurchaseOrderNumber?: string | null;
      ShipToAddress1?: string | null;
      ShipToAddress2?: string | null;
      ShipToCity?: string | null;
      ShipToName?: string | null;
      ShipToPostalCode?: string | null;
      ShipToProvince?: string | null;
      ShipVia?: string | null;
      ShipViaDescription?: string | null;
      SubTotal?: number;
      TermsCode?: string | null;
      VendorCode?: string | null;
      VendorName?: string | null;
      PartNumbers?: unknown[] | null;
      Receipts?: unknown[] | null;
    };

    const purchaseOrder = new PurchaseOrder();

    purchaseOrder.billToAddress1 = src.BillToAddress1 ?? undefined;
    purchaseOrder.billToAddress2 = src.BillToAddress2 ?? undefined;
    purchaseOrder.billToCity = src.BillToCity ?? undefined;
    purchaseOrder.billToName = src.BillToName ?? undefined;
    purchaseOrder.billToPostalCode = src.BillToPostalCode ?? undefined;
    purchaseOrder.billToProvince = src.BillToProvince ?? undefined;
    purchaseOrder.comments = src.Comments ?? undefined;
    purchaseOrder.contactEmail = src.ContactEmail ?? undefined;
    purchaseOrder.expectedArrivalDate = src.ExpectedArrivalDate;
    purchaseOrder.location = src.Location ?? undefined;
    purchaseOrder.manufacturerId = src.ManufacturerId ?? undefined;
    purchaseOrder.manufacturerName = src.ManufacturerName ?? undefined;
    purchaseOrder.purchaseOrderDate = src.PurchaseOrderDate;
    purchaseOrder.purchaseOrderNumber = src.PurchaseOrderNumber ?? undefined;
    purchaseOrder.shipToAddress1 = src.ShipToAddress1 ?? undefined;
    purchaseOrder.shipToAddress2 = src.ShipToAddress2 ?? undefined;
    purchaseOrder.shipToCity = src.ShipToCity ?? undefined;
    purchaseOrder.shipToName = src.ShipToName ?? undefined;
    purchaseOrder.shipToPostalCode = src.ShipToPostalCode ?? undefined;
    purchaseOrder.shipToProvince = src.ShipToProvince ?? undefined;
    purchaseOrder.shipVia = src.ShipVia ?? undefined;
    purchaseOrder.shipViaDescription = src.ShipViaDescription ?? undefined;
    purchaseOrder.subTotal = src.SubTotal;
    purchaseOrder.termsCode = src.TermsCode ?? undefined;
    purchaseOrder.vendorCode = src.VendorCode ?? undefined;
    purchaseOrder.vendorName = src.VendorName ?? undefined;

    if (src.PartNumbers) {
      purchaseOrder.partNumbers = src.PartNumbers.map(PurchaseOrderPartNumber.convertFrom);
    }
    if (src.Receipts) {
      purchaseOrder.receipts = src.Receipts.map(Receipt.convertFrom);
    }


    return purchaseOrder;
  }
}

export class PurchaseOrderPartNumber {

  public randmarSKU?: string;
  public description?: string;
  public location?: string;
  public quantity?: number;
  public mpn?: string;
  public unitPrice?: number;
  public category?: string;
  public purchaseOrderNumber?: string;
  public purchaseOrderDate?: number;
  public lineNumber?: number;

  public static convertFrom(source: unknown): PurchaseOrderPartNumber {
    const record = (source && typeof source === "object" ? (source as Record<string, unknown>) : {}) as Record<string, unknown>;
    const readString = (...keys: string[]) => {
      for (const key of keys) {
        const value = record[key];
        if (typeof value === "string") return value;
      }
      return undefined;
    };
    const readNumber = (...keys: string[]) => {
      for (const key of keys) {
        const value = record[key];
        if (typeof value === "number") return value;
        if (typeof value === "string" && value.trim() !== "") {
          const parsed = Number(value);
          if (!Number.isNaN(parsed)) return parsed;
        }
      }
      return undefined;
    };

    const partNumber = new PurchaseOrderPartNumber();
    partNumber.randmarSKU = readString("RandmarSKU", "randmarSKU");
    partNumber.description = readString("Description", "description");
    partNumber.location = readString("Location", "location");
    partNumber.mpn = readString("MPN", "mpn");
    partNumber.unitPrice = readNumber("UnitPrice", "unitPrice");
    partNumber.quantity = readNumber("QuantityOutstanding", "quantityOutstanding", "QuantityOrdered", "quantityOrdered", "Quantity", "quantity");
    partNumber.category = readString("Category", "category", "CategoryName", "categoryName");
    partNumber.purchaseOrderNumber = readString("PurchaseOrderNumber", "purchaseOrderNumber");
    partNumber.purchaseOrderDate = readNumber("PurchaseOrderDate", "purchaseOrderDate");
    partNumber.lineNumber = readNumber("LineNumber", "lineNumber");

    return partNumber;
  }
}

export class Receipt implements IDocument {
  receiptNumber?: string;
  invoiceNumber?: string;
  location?: string;
  vendorName?: string;
  receiptDate?: number;
  purchaseOrderNumber?: string;
  comments?: string;
  orders?: OrderDetail[];
  partNumbers?: ReceiptPartNumber[];
  invoiced?: boolean;
  subTotal?: number;
  taxAmount1?: number;
  taxAmount2?: number;
  total?: number;

  public static convertFrom(source: unknown): Receipt {
    const src = source as {
      InvoiceNumber?: string | null;
      ReceiptNumber?: string | null;
      VendorName?: string | null;
      ReceiptDate?: number;
      Location?: string | null;
      PurchaseOrderNumber?: string | null;
      Comments?: string | null;
      Invoiced?: boolean;
      SubTotal?: number;
      TaxAmount1?: number;
      TaxAmount2?: number;
      Total?: number;
      Orders?: unknown[] | null;
      PartNumbers?: unknown[] | null;
    };

    const receipt = new Receipt();

    receipt.invoiceNumber = src.InvoiceNumber ?? undefined;
    receipt.receiptNumber = src.ReceiptNumber ?? undefined;
    receipt.vendorName = src.VendorName ?? undefined;
    receipt.receiptDate = src.ReceiptDate;
    receipt.location = src.Location ?? undefined;
    receipt.purchaseOrderNumber = src.PurchaseOrderNumber ?? undefined;
    receipt.comments = src.Comments ?? undefined;
    receipt.invoiced = src.Invoiced;
    receipt.subTotal = src.SubTotal;
    receipt.taxAmount1 = src.TaxAmount1;
    receipt.taxAmount2 = src.TaxAmount2;
    receipt.total = src.Total;

    if (src.Orders)
      receipt.orders = src.Orders.map((o) => OrderDetail.convertFrom(o));
    if (src.PartNumbers)
      receipt.partNumbers = src.PartNumbers.map(ReceiptPartNumber.convertFrom);

    return receipt;
  }
}

export class ReceiptPartNumber {

  public randmarSKU?: string;
  public description?: string;
  public location?: string;
  public quantity?: number;
  public receiptNumber?: string;
  public mpn?: string;
  public unitPrice?: number;

  public static convertFrom(source: unknown): ReceiptPartNumber {
    const src = source as {
      RandmarSKU?: string | null;
      Description?: string | null;
      Location?: string | null;
      Quantity?: number;
      ReceiptNumber?: string | null;
      MPN?: string | null;
      UnitPrice?: number;
    };

    const partNumber = new ReceiptPartNumber();
    partNumber.randmarSKU = src.RandmarSKU ?? undefined;
    partNumber.description = src.Description ?? undefined;
    partNumber.location = src.Location ?? undefined;
    partNumber.mpn = src.MPN ?? undefined;
    partNumber.unitPrice = src.UnitPrice;
    partNumber.receiptNumber = src.ReceiptNumber ?? undefined;
    partNumber.quantity = src.Quantity;
    return partNumber;
  }
}

export class Requisition implements IDocument {
  location?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  reference?: string;
  requestedBy?: string;
  requiredByDate?: number;
  requisitionDate?: number;
  requisitionNumber?: string;
  subTotal?: number;
  partNumbers?: RequisitionPartNumber[];

  public static convertFrom(source: unknown): Requisition {
    const src = source as {
      Location?: string | null;
      ManufacturerId?: string | null;
      ManufacturerName?: string | null;
      Reference?: string | null;
      RequestedBy?: string | null;
      RequiredByDate?: number;
      RequisitionDate?: number;
      RequisitionNumber?: string | null;
      SubTotal?: number;
      PartNumbers?: unknown[] | null;
    };

    const requisition = new Requisition();

    requisition.location = src.Location ?? undefined;
    requisition.manufacturerId = src.ManufacturerId ?? undefined;
    requisition.manufacturerName = src.ManufacturerName ?? undefined;
    requisition.reference = src.Reference ?? undefined;
    requisition.requestedBy = src.RequestedBy ?? undefined;
    requisition.requiredByDate = src.RequiredByDate;
    requisition.requisitionDate = src.RequisitionDate;
    requisition.requisitionNumber = src.RequisitionNumber ?? undefined;
    requisition.subTotal = src.SubTotal;

    if (src.PartNumbers)
      requisition.partNumbers = src.PartNumbers.map(RequisitionPartNumber.convertFrom);

    return requisition;
  }
}

export class RequisitionPartNumber {

  public randmarSKU?: string;
  public description?: string;
  public location?: string;
  public quantityOrdered?: number;
  public requisitionNumber?: string;
  public mpn?: string;
  public unitPrice?: number;

  public static convertFrom(source: unknown): RequisitionPartNumber {
    const record = (source && typeof source === "object" ? (source as Record<string, unknown>) : {}) as Record<string, unknown>;
    const readString = (...keys: string[]) => {
      for (const key of keys) {
        const value = record[key];
        if (typeof value === "string") return value;
      }
      return undefined;
    };
    const readNumber = (...keys: string[]) => {
      for (const key of keys) {
        const value = record[key];
        if (typeof value === "number") return value;
        if (typeof value === "string" && value.trim() !== "") {
          const parsed = Number(value);
          if (!Number.isNaN(parsed)) return parsed;
        }
      }
      return undefined;
    };

    const partNumber = new RequisitionPartNumber();
    partNumber.randmarSKU = readString("RandmarSKU", "randmarSKU");
    partNumber.description = readString("Description", "description");
    partNumber.location = readString("Location", "location");
    partNumber.mpn = readString("MPN", "mpn");
    partNumber.requisitionNumber = readString("RequisitionNumber", "requisitionNumber", "DocumentNumber", "documentNumber");
    partNumber.unitPrice = readNumber("UnitPrice", "unitPrice");
    partNumber.quantityOrdered = readNumber("QuantityOrdered", "quantityOrdered", "Quantity", "quantity");
    return partNumber;
  }
}

export class Invoice implements IDocument {
  manufacturerId?: string;
  manufacturerName?: string;
  invoiceDate?: number;
  invoiceNumber?: string;
  purchaseOrderNumber?: string;
  dueDate?: number;
  taxAmount1?: number;
  taxRate1?: number;
  taxAmount2?: number;
  taxRate2?: number;
  subTotal?: number;
  total?: number;
  payed?: boolean;
  documentType?: string;

  public static convertFrom(source: ApiInvoice): Invoice {
    const invoice = new Invoice();

    invoice.manufacturerId = source.ManufacturerId ?? undefined;
    invoice.manufacturerName = undefined;
    invoice.invoiceDate = source.InvoiceDate;
    invoice.invoiceNumber = source.InvoiceNumber ?? undefined;
    invoice.purchaseOrderNumber = source.PONumber ?? undefined;
    invoice.dueDate = undefined;
    invoice.taxAmount1 = source.TaxAmount1;
    invoice.taxRate1 = source.TaxRate1;
    invoice.taxAmount2 = source.TaxAmount2;
    invoice.taxRate2 = source.TaxRate2;
    invoice.subTotal = source.SubTotal;
    invoice.total = source.Total;
    invoice.payed = source.Payed;
    invoice.documentType = source.LineType ?? undefined;

    return invoice;
  }
}

export class OrderDetail {
  category?: string;
  documentDate?: number;
  orderDate?: number;
  documentNumber?: string;
  documentType?: string;
  extendedPrice?: number;
  extendedWeight?: number;
  mpn?: string;
  manufacturerId?: string;
  opportunityManufacTurerId?: string;
  opportunityManufacturerNumber?: string;
  orderNumber?: string;
  poNumber?: string;
  manufacturerName?: string;
  quantity?: number;
  randmarSKU?: string;
  resellerId?: string;
  resellerName?: string;
  shipToName?: string;
  shipViaDescription?: string;
  unitPrice?: number;
  unitWeight?: number;
  warehouseCode?: string;
  shipToZipCode?: string;

  public static convertFrom(source: unknown): OrderDetail {
    return OrderDetail.convertFromApi(source as ApiOrderDetail);
  }

  public static convertFromApi(source: ApiOrderDetail): OrderDetail {
    const orderDetail = new OrderDetail();

    orderDetail.category = source.Category ?? undefined;
    orderDetail.orderDate = source.OrderDate;
    orderDetail.documentDate = source.DocumentDate;
    orderDetail.documentNumber = source.DocumentNumber ?? undefined;
    orderDetail.documentType = source.DocumentType ?? undefined;
    orderDetail.extendedPrice = source.ExtendedPrice;
    orderDetail.extendedWeight = source.ExtendedWeight;
    orderDetail.mpn = source.MPN ?? undefined;
    orderDetail.manufacturerId = source.ManufacturerId ?? undefined;
    orderDetail.opportunityManufacTurerId = source.OpportunityManufacTurerId ?? undefined;
    orderDetail.opportunityManufacturerNumber = source.OpportunityManufacturerNumber ?? undefined;
    orderDetail.orderNumber = source.OrderNumber ?? undefined;
    orderDetail.poNumber = source.PONumber ?? undefined;
    orderDetail.manufacturerName = source.ManufacturerName ?? undefined;
    orderDetail.quantity = source.Quantity;
    orderDetail.randmarSKU = source.RandmarSKU ?? undefined;
    orderDetail.resellerId = source.ResellerId ?? undefined;
    orderDetail.resellerName = source.ResellerName ?? undefined;
    orderDetail.shipToName = source.ShipToName ?? undefined;
    orderDetail.shipToZipCode = source.ShipToZipCode ?? undefined;
    orderDetail.shipViaDescription = source.ShipViaDescription ?? undefined;
    orderDetail.unitPrice = source.UnitPrice;
    orderDetail.unitWeight = source.UnitWeight;
    orderDetail.warehouseCode = source.WarehouseCode ?? undefined;

    return orderDetail;
  }

  public toApiType(): ApiOrderDetail {
    return {
      Category: this.category,
      DocumentDate: this.documentDate,
      DocumentNumber: this.documentNumber,
      DocumentType: this.documentType,
      ExtendedPrice: this.extendedPrice,
      ExtendedWeight: this.extendedWeight,
      MPN: this.mpn,
      ManufacturerId: this.manufacturerId,
      ManufacturerName: this.manufacturerName,
      OpportunityManufacTurerId: this.opportunityManufacTurerId,
      OpportunityManufacturerNumber: this.opportunityManufacturerNumber,
      OrderDate: this.orderDate,
      OrderNumber: this.orderNumber,
      PONumber: this.poNumber,
      Quantity: this.quantity,
      RandmarSKU: this.randmarSKU,
      ResellerId: this.resellerId,
      ResellerName: this.resellerName,
      ShipToName: this.shipToName,
      ShipToZipCode: this.shipToZipCode,
      ShipViaDescription: this.shipViaDescription,
      UnitPrice: this.unitPrice,
      UnitWeight: this.unitWeight,
      WarehouseCode: this.warehouseCode,
    };
  }
}

export class GeneralDocument {
  allowPartialShipment?: boolean;
  bidNumber?: string;
  endUserPO?: string;
  enteredBy?: string;
  manufacturerId?: string;
  orderDate?: string;
  activeOrderDetails?: OrderDetail[];
  completedSalesOrderDetails?: OrderDetail[];
  orderNumber?: string;
  pONumber?: string;
  resellerId?: number;
  shipAddress1?: string;
  shipAddress2?: string;
  shipCity?: string;
  shipContact?: string;
  shipCountry?: string;
  shipEmail?: string;
  shipName?: string;
  shippingSlipComment?: string
  shipProvince?: string;
  shipViaCode?: string;
  shipZip?: string;
  reseller?: Reseller;

  public static convertFrom(source: ApiGeneralDocument): GeneralDocument {
    const generalDocument = new GeneralDocument();

    generalDocument.allowPartialShipment = source.AllowPartialShipment;
    generalDocument.endUserPO = source.EndUserPO ?? undefined;
    generalDocument.enteredBy = source.EnteredBy ?? undefined;
    generalDocument.orderDate = source.OrderDate ?? undefined;
    generalDocument.orderNumber = source.OrderNumber ?? undefined;
    generalDocument.pONumber = source.PONumber ?? undefined;
    generalDocument.resellerId = source.ResellerId ? Number.parseInt(source.ResellerId, 10) : undefined;
    generalDocument.shipAddress1 = source.ShipAddress1 ?? undefined;
    generalDocument.shipAddress2 = source.ShipAddress2 ?? undefined;
    generalDocument.shipCity = source.ShipCity ?? undefined;
    generalDocument.shipContact = source.ShipContact ?? undefined;
    generalDocument.shipCountry = source.ShipCountry ?? undefined;
    generalDocument.shipEmail = source.ShipEmail ?? undefined;
    generalDocument.shipName = source.ShipName ?? undefined;
    generalDocument.shippingSlipComment = source.ShippingSlipComment ?? undefined;
    generalDocument.shipProvince = source.ShipProvince ?? undefined;
    generalDocument.shipViaCode = source.ShipViaCode ?? undefined;
    generalDocument.shipZip = source.ShipZip ?? undefined;

    if (source.Reseller)
      generalDocument.reseller = Reseller.convertFrom(source.Reseller);

    if (source.ActiveOrderDetails)
      generalDocument.activeOrderDetails = source.ActiveOrderDetails.map(OrderDetail.convertFromApi);

    if (source.CompletedOrderDetails)
      generalDocument.completedSalesOrderDetails = source.CompletedOrderDetails.map(OrderDetail.convertFromApi);

    return generalDocument;
  }
}
